import { supabase } from './supabase'

// ─── Tenant ───────────────────────────────────────────────────────────────────

let _tenantId = null
let _tenantDetails = null

async function loadTenantFromProfile() {
  const { data: { user } } = await supabase.auth.getUser()
  if (!user) throw new Error('Not authenticated')
  const { data: profile, error: pe } = await supabase
    .from('profiles').select('tenant_id').eq('id', user.id).single()
  if (pe) throw new Error('Could not load profile: ' + pe.message)
  _tenantId = profile.tenant_id
  const { data: tenant, error: te } = await supabase
    .from('tenants').select('name, contact_phone, contact_email, address')
    .eq('id', _tenantId).single()
  if (te) throw new Error('Could not load tenant: ' + te.message)
  _tenantDetails = tenant
}

export async function getTenantId() {
  if (!_tenantId) await loadTenantFromProfile()
  return _tenantId
}

export async function getTenantDetails() {
  if (!_tenantDetails) await loadTenantFromProfile()
  return _tenantDetails
}

export function resetTenantCache() {
  _tenantId = null
  _tenantDetails = null
}

// ─── Helpers ──────────────────────────────────────────────────────────────────

function todayISO() {
  return new Date().toISOString().slice(0, 10)
}

function dateToQuoteStr(isoDate = todayISO()) {
  return isoDate.replace(/-/g, '')
}

function pad3(n) {
  return String(n).padStart(3, '0')
}

// ─── Quote numbering ──────────────────────────────────────────────────────────

// Show the next quote number without consuming it (for preview display)
export async function peekNextQuoteNo() {
  const tenantId = await getTenantId()
  const today = todayISO()

  const { data } = await supabase
    .from('quote_counters')
    .select('counter')
    .eq('tenant_id', tenantId)
    .eq('counter_date', today)
    .maybeSingle()

  const next = ((data?.counter) ?? 0) + 1
  return `DQ-${dateToQuoteStr(today)}-${pad3(next)}`
}

// Atomically increment and consume the next quote number
async function consumeQuoteNumber() {
  const tenantId = await getTenantId()
  const today = todayISO()

  const { data: counter, error: counterError } = await supabase
    .rpc('increment_quote_counter', {
      p_tenant_id: tenantId,
      p_date: today
    })
  if (counterError) throw new Error('Counter error: ' + counterError.message)

  // base_number = total original quotes so far + 1
  const { count, error: countError } = await supabase
    .from('quotes')
    .select('*', { count: 'exact', head: true })
    .eq('tenant_id', tenantId)
    .eq('revision', 0)
  if (countError) throw new Error('Count error: ' + countError.message)

  const baseNumber = (count ?? 0) + 1
  const displayQuoteNo = `DQ-${dateToQuoteStr(today)}-${pad3(counter)}`

  return { baseNumber, displayQuoteNo, tenantId }
}

// ─── Save new quote (called on first print) ───────────────────────────────────

export async function saveNewQuote(appState, grandTotal, toBePaid) {
  const { baseNumber, displayQuoteNo, tenantId } = await consumeQuoteNumber()

  const { data, error } = await supabase
    .from('quotes')
    .insert({
      tenant_id: tenantId,
      base_number: baseNumber,
      revision: 0,
      display_quote_no: displayQuoteNo,
      parent_id: null,
      customer_name: appState.header?.customerName?.trim() || 'Unknown',
      address: appState.header?.address?.trim() || '',
      quote_date: todayISO(),
      grand_total: grandTotal,
      to_be_paid: toBePaid,
      status: 'Quoted',
      quote_data: appState,
      is_latest_revision: true
    })
    .select('id')
    .single()
  if (error) throw new Error('Save failed: ' + error.message)

  return { id: data.id, baseNumber, displayQuoteNo }
}

// ─── Save revision (called on first print of a loaded-for-revision quote) ────

export async function saveRevision(appState, grandTotal, toBePaid, parentQuote) {
  const tenantId = await getTenantId()

  const { data: maxRow, error: maxError } = await supabase
    .from('quotes')
    .select('revision')
    .eq('tenant_id', tenantId)
    .eq('base_number', parentQuote.base_number)
    .order('revision', { ascending: false })
    .limit(1)
    .single()

  if (maxError) throw new Error('Could not fetch max revision: ' + maxError.message)
  const newRevision = maxRow.revision + 1

  // Strip any existing .N suffix and append new revision number
  const baseDisplayNo = parentQuote.display_quote_no.split('.')[0]
  const displayQuoteNo = `${baseDisplayNo}.${newRevision}`

  // INSERT new revision first — if this fails, original row is untouched
  const { data, error } = await supabase
    .from('quotes')
    .insert({
      tenant_id: tenantId,
      base_number: parentQuote.base_number,
      revision: newRevision,
      display_quote_no: displayQuoteNo,
      parent_id: parentQuote.id,
      customer_name: appState.header?.customerName?.trim() || 'Unknown',
      address: appState.header?.address?.trim() || '',
      quote_date: todayISO(),
      grand_total: grandTotal,
      to_be_paid: toBePaid,
      status: 'Quoted',
      quote_data: appState,
      is_latest_revision: true
    })
    .select('id')
    .single()
  if (error) throw new Error('Revision save failed: ' + error.message)

  // Only mark previous revisions as not latest after INSERT succeeds
  await supabase
    .from('quotes')
    .update({ is_latest_revision: false })
    .eq('tenant_id', tenantId)
    .eq('base_number', parentQuote.base_number)
    .neq('id', data.id)

  return { id: data.id, displayQuoteNo }
}

// ─── Load quotes list (for landing screen) ────────────────────────────────────

export async function loadQuotesWithData() {
  const tenantId = await getTenantId()
  const { data, error } = await supabase
    .from('quotes')
    .select('id, display_quote_no, customer_name, address, quote_date, grand_total, to_be_paid, status, base_number, revision, created_at, advance_paid, quote_data')
    .eq('tenant_id', tenantId)
    .eq('is_latest_revision', true)
    .order('created_at', { ascending: false })
  if (error) throw new Error('Load failed: ' + error.message)
  return data
}

export async function loadQuotesList() {
  const tenantId = await getTenantId()
  const { data, error } = await supabase
    .from('quotes')
    .select('id, display_quote_no, customer_name, address, quote_date, grand_total, to_be_paid, status, base_number, revision, created_at, advance_paid')
    .eq('tenant_id', tenantId)
    .eq('is_latest_revision', true)
    .order('created_at', { ascending: false })
  if (error) throw new Error('Load failed: ' + error.message)
  return data
}

// ─── Load single quote (for revision) ────────────────────────────────────────

export async function loadQuoteById(id) {
  const { data, error } = await supabase
    .from('quotes')
    .select('*')
    .eq('id', id)
    .single()
  if (error) throw new Error('Could not load quote: ' + error.message)
  return data
}

// ─── Load revision history of a quote ─────────────────────────────────────────

export async function loadRevisionHistory(baseNumber) {
  const tenantId = await getTenantId()
  const { data, error } = await supabase
    .from('quotes')
    .select('id, display_quote_no, revision, created_at, grand_total, status')
    .eq('tenant_id', tenantId)
    .eq('base_number', baseNumber)
    .order('revision', { ascending: true })
  if (error) throw new Error('History load failed: ' + error.message)
  return data
}

// ─── Update status ────────────────────────────────────────────────────────────

export async function updateQuoteStatus(id, status) {
  const { error } = await supabase
    .from('quotes')
    .update({ status })
    .eq('id', id)
  if (error) throw new Error('Status update failed: ' + error.message)
}

// ─── Update advance_paid ──────────────────────────────────────────────────────

export async function updateAdvancePaid(id, paid) {
  const { data, error } = await supabase
    .from('quotes')
    .update({ advance_paid: paid })
    .eq('id', id)
    .select('id, advance_paid')
  if (error) throw new Error('Advance paid update failed: ' + error.message)
  if (!data || data.length === 0) throw new Error('No rows updated — possible RLS rejection')
}